import { z } from 'zod'

const timeSchema = z.object({
  hour: z.number().int().min(1).max(12),
  minute: z.number().int().min(0).max(59),
  period: z.enum(['AM', 'PM']),
})

export const eventSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    theme: z.string().optional(),
    description: z.string().optional(),
    startDate: z.date({ required_error: 'Start date is required' }),
    startTime: timeSchema,
    endDate: z.date({ required_error: 'End date is required' }),
    endTime: timeSchema,
    variant: z.enum(['blue', 'green', 'red', 'yellow', 'purple', 'orange']),
    deliveryMode: z.enum(['in_person', 'online']),
    location: z.string().optional(),
    onlineLink: z
      .union([z.string().url('Enter a valid URL'), z.literal('')])
      .optional(),
    points: z
      .number({ invalid_type_error: 'Enter the CPD points awarded' })
      .min(0, 'Points cannot be negative'),
  })
  .refine(
    data => {
      if (data.deliveryMode === 'online') {
        return Boolean(data.onlineLink && data.onlineLink.trim())
      }
      return true
    },
    {
      message: 'Provide the meeting link for online events',
      path: ['onlineLink'],
    },
  )
  .refine(
    data => {
      if (data.deliveryMode === 'in_person') {
        return Boolean(data.location && data.location.trim())
      }
      return true
    },
    {
      message: 'Location is required for in-person events',
      path: ['location'],
    },
  )
  .refine(
    data => {
      const toDateTime = (date: Date, time: { hour: number; minute: number; period: 'AM' | 'PM' }) => {
        const result = new Date(date)
        let hour = time.hour % 12
        if (time.period === 'PM') {
          hour += 12
        }
        if (time.period === 'AM' && time.hour === 12) {
          hour = 0
        }
        if (time.period === 'PM' && time.hour === 12) {
          hour = 12
        }
        result.setHours(hour, time.minute, 0, 0)
        return result
      }

      const start = toDateTime(new Date(data.startDate), data.startTime)
      const end = toDateTime(new Date(data.endDate), data.endTime)

      return end.getTime() > start.getTime()
    },
    {
      message: 'End time must be after the start time',
      path: ['endTime'],
    },
  )

export type TEventFormData = z.infer<typeof eventSchema>
